<template>
  <div class="empty-state-wrapper">
    <div class="empty-state-illustration">
      <img
          v-if="imageLoaded"
          :src="image"
          :alt="title"
          class="empty-state-image"
          @error="onImageError"
          @load="onImageLoad"
      >
      <div v-else class="empty-state-icon">
        <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="60" cy="60" r="60" fill="#f8fafc"/>
          <circle cx="75" cy="45" r="12" fill="#22c55e"/>
          <path d="M75 40V50M70 45H80" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
          <rect x="45" y="55" width="24" height="30" rx="2" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
          <rect x="47" y="57" width="20" height="6" rx="1" fill="#3b82f6"/>
          <rect x="47" y="66" width="12" height="1.5" rx="0.5" fill="#cbd5e1"/>
          <rect x="47" y="69" width="15" height="1.5" rx="0.5" fill="#cbd5e1"/>
          <rect x="47" y="72" width="18" height="1.5" rx="0.5" fill="#cbd5e1"/>
          <rect x="47" y="75" width="9" height="1.5" rx="0.5" fill="#cbd5e1"/>
          <rect x="47" y="78" width="13" height="1.5" rx="0.5" fill="#cbd5e1"/>
        </svg>
      </div>
    </div>

    <h3 class="empty-state-title">{{ title }}</h3>

    <button v-if="showAction" class="empty-state-action" @click="$emit('action')">
      {{ actionText }}
    </button>
  </div>
</template>

<script>
export default {
  name: 'EmptyState',
  props: {
    image: {
      type: String,
      default: () => {
        try {
          return require('@/assets/images/noData.png')
        } catch (error) {
          console.warn('noData.png not found, using fallback')
          return null
        }
      }
    },
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    showAction: {
      type: Boolean,
      default: false
    },
    actionText: {
      type: String,
      default: 'Add New'
    }
  },
  emits: ['action'],
  data() {
    return {
      imageLoaded: false
    }
  },
  mounted() {
    if (this.image) {
      this.checkImage()
    }
  },
  methods: {
    checkImage() {
      if (!this.image) {
        this.imageLoaded = false
        return
      }

      const img = new Image()
      img.onload = () => {
        this.imageLoaded = true
      }
      img.onerror = () => {
        console.warn('Image failed to load:', this.image)
        this.imageLoaded = false
      }
      img.src = this.image
    },
    onImageLoad() {
      this.imageLoaded = true
    },
    onImageError() {
      console.warn('Image error:', this.image)
      this.imageLoaded = false
    }
  }
}
</script>

<style scoped>
.empty-state-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  text-align: center;
  width: 100%;
  min-height: 400px;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 20px;
}

.empty-state-illustration {
  margin-bottom: 32px;
  position: relative;
  animation: fadeInUp 0.6s ease-out;
}

.empty-state-image {
  width: 120px;
  height: auto;
  opacity: 0.9;
  transition: all 0.3s ease;
}

.empty-state-icon {
  opacity: 0.9;
  transition: all 0.3s ease;
}

.empty-state-icon:hover {
  transform: translateY(-2px);
  opacity: 1;
}

.empty-state-title {
  font-family: euclid_medium, sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px 0;
  line-height: 1.3;
  animation: fadeInUp 0.8s ease-out;
}

.empty-state-action {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  animation: fadeInUp 1.2s ease-out;
}

.empty-state-action:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
}

.empty-state-action:active {
  transform: translateY(0);
}

@media (max-width: 768px) {
  .empty-state-wrapper {
    min-height: 350px;
  }

  .empty-state-illustration {
    margin-bottom: 24px;
  }

  .empty-state-image {
    width: 100px;
  }

  .empty-state-title {
    font-size: 20px;
    margin-bottom: 8px;
  }
}

@media (max-width: 480px) {
  .empty-state-wrapper {
    padding: 40px 16px;
    min-height: 300px;
  }

  .empty-state-title {
    font-size: 18px;
  }
}

@media (prefers-color-scheme: dark) {
  .empty-state-wrapper {
    background-color: rgb(255, 255, 255);
  }

  .empty-state-title {
    color: #667085;
  }

}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.empty-state-action:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}
</style>
